import { format } from "date-fns";
import { Plus, Pencil, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ItemCard } from "@/components/items/ItemCard";
import type { CalendarEvent } from "@/hooks/useCalendar";
import type { Item } from "@/lib/types";

type Props = {
  date: Date;
  events: CalendarEvent[];
  tasks: Item[];
  onAddEvent: () => void;
  onTaskClick: (item: Item) => void;
  onEventClick: (event: CalendarEvent) => void;
};

export function DayDetail({ date, events, tasks, onAddEvent, onTaskClick, onEventClick }: Props) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">
          {format(date, "EEEE, MMMM d")}
        </h3>
        <Button variant="outline" size="sm" onClick={onAddEvent}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Event
        </Button>
      </div>

      {/* Calendar events */}
      {events.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Events
          </p>
          {events.map((event) => (
            <button
              key={event.id}
              onClick={() => onEventClick(event)}
              className="w-full text-left flex items-start gap-3 rounded-lg border border-border bg-card p-3 hover:border-primary/30 transition-colors group"
            >
              <div className="w-1 self-stretch min-h-[2rem] rounded-full bg-blue-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm line-clamp-1">
                    {event.title || "(No title)"}
                  </span>
                  <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  {event.all_day ? (
                    <span>All day</span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {format(new Date(event.start), "h:mm a")} -{" "}
                      {format(new Date(event.end), "h:mm a")}
                    </span>
                  )}
                  {event.location && (
                    <span className="flex items-center gap-1 line-clamp-1">
                      <MapPin className="h-3 w-3 shrink-0" />
                      {event.location}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Tasks */}
      {tasks.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Tasks
          </p>
          {tasks.map((task) => (
            <ItemCard
              key={task.id}
              item={task}
              onClick={() => onTaskClick(task)}
              compact
            />
          ))}
        </div>
      )}

      {events.length === 0 && tasks.length === 0 && (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Nothing scheduled for this day
        </p>
      )}
    </div>
  );
}
